import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Check, X } from 'lucide-react';
import './ContactSection.css';

// WhatsApp Icon Component
const WhatsAppIcon: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const whatsappNumber = "+917908086574";
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^\d]/g, '')}`;
    
    // Use the same scroll logic as the sidebar - appears when scrolled more than 100px from top
    React.useEffect(() => {
        const handleScroll = () => {
            // Show WhatsApp icon when scrolled more than 100px from top (same as sidebar)
            if (window.scrollY > 100) {
                setIsVisible(true);
            } else {
                setIsVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    
    return (
        <AnimatePresence mode="wait">
            {isVisible && (
                <motion.a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-float"
                    aria-label="Chat on WhatsApp"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ 
                        opacity: 1,
                        scale: 1
                    }}
                    exit={{ 
                        opacity: 0,
                        scale: 0.8
                    }}
                    transition={{ 
                        duration: 0.25,
                        ease: [0.4, 0.0, 0.2, 1]
                    }}
                    style={{
                        transform: 'translateZ(0)'
                    }}
                >
                    <svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.149-.67.149-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.123-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                </motion.a>
            )}
        </AnimatePresence>
    );
};

const ContactSection: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        budget: '',
        date: '',
        message: ''
    });

    // Google Apps Script Web App URL
    const GOOGLE_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby3dy0kpM3ybidMI9-ulxDuOsbcEn9QhjhoDqHP1vF7NrAoJwrbKJ88JGwMCfUuYSW8PQ/exec';

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        // Validate name field - allow only letters and spaces
        if (name === 'name') {
            const lettersOnly = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: lettersOnly
            }));
        } 
        // Validate phone field - allow only digits, minimum 10 digits
        else if (name === 'phone') {
            const digitsOnly = value.replace(/\D/g, ''); // Remove all non-digits
            setFormData(prev => ({
                ...prev,
                [name]: digitsOnly
            }));
        } 
        else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate name has at least 2 words
        const nameWords = formData.name.trim().split(/\s+/);
        if (nameWords.length < 2) {
            alert('Please enter at least 2 words for your name (First and Last name)');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }
        
        // Validate phone number has at least 10 digits
        if (formData.phone.length < 10) {
            alert('Please enter at least 10 digits for the phone number');
            return;
        }
        
        setIsSubmitting(true);

        try {
            const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
                method: 'POST',
                mode: 'no-cors', // Required for Google Apps Script
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    service: formData.service,
                    budget: formData.budget,
                    message: formData.message
                })
            });

            // Since mode is 'no-cors', we can't read the response
            // We'll assume success after a short delay
            setTimeout(() => {
                setIsSubmitting(false);
                setShowSuccessModal(true);
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    service: '',
                    budget: '',
                    date: '',
                    message: ''
                });
                // Reset form fields
                const form = e.target as HTMLFormElement;
                form.reset();
            }, 2000);
        } catch (error) {
            console.error('Form submission error:', error);
            setIsSubmitting(false);
            // Still show success modal for better UX (Google Apps Script often works despite CORS errors)
            setShowSuccessModal(true);
        }
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
    };

    // Helper function to wrap punctuation and numbers with default font
    const wrapWithDefaultFont = (text: string) => {
        return text.split(/([.,;:!?'"()[\]{}0-9]+)/).map((part, index) => {
            if (/[.,;:!?'"()[\]{}]/.test(part) || /\d/.test(part)) {
                return <span key={index} className="default-font">{part}</span>;
            }
            return part;
        });
    };

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    // Parallax and reveal animations
    const titleY = useTransform(scrollYProgress, [0.1, 0.4], [100, 0]);
    const titleOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

    const formY = useTransform(scrollYProgress, [0.2, 0.5], [100, 0]);
    const formOpacity = useTransform(scrollYProgress, [0.2, 0.4], [0, 1]);

    const imageScale = useTransform(scrollYProgress, [0.1, 0.6], [1.2, 1]);
    const imageOpacity = useTransform(scrollYProgress, [0.1, 0.4], [0, 1]);

    return (
        <div className="contact-section" id="contact" ref={containerRef}>
            <div className="contact-container">
                <div className="contact-grid">
                    {/* Left Content - Typography */}
                    <div className="contact-left">
                        <motion.div
                            className="contact-header"
                            style={{ y: titleY, opacity: titleOpacity }}
                        >
                            <h2 className="contact-title">
                                LET'S CREATE<br />
                                <span className="title-highlight">MAGIC</span> TOGETHER
                            </h2>
                            <p className="contact-subtitle">
                                Your story deserves to be told with cinematic excellence.
                                Reach out to begin your journey with Studio Z26.
                            </p>
                        </motion.div>

                        <motion.div
                            className="contact-image-wrapper"
                            style={{ scale: imageScale, opacity: imageOpacity }}
                        >
                            <div className="contact-image-card">
                                <div className="contact-info-container">
                                    {/* Left side - Contact Cards */}
                                    <div className="contact-cards">
                                        <a href="tel:917908086574" className="contact-card-link">
                                            <div className="contact-card">
                                                <div className="card-icon">
                                                    <Phone size={24} />
                                                </div>
                                                <div className="card-content">
                                                    <h4>{wrapWithDefaultFont("PHONE ")}</h4>
                                                    <p>+91 79080 86574</p>
                                                </div>
                                            </div>
                                        </a>
                                        
                                        <a href="mailto:quick.studioz26@gmail.com" className="contact-card-link">
                                            <div className="contact-card">
                                                <div className="card-icon">
                                                    <Mail size={24} />
                                                </div>
                                                <div className="card-content">
                                                    <h4>{wrapWithDefaultFont("EMAIL")}</h4>
                                                    <p>quick.studioz26@gmail.com</p>
                                                </div>
                                            </div>
                                        </a>
                                        
                                        <a href="https://www.google.com/maps?q=23.386728599999998,88.5009809" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                                            <div className="contact-card">
                                                <div className="card-icon">
                                                    <MapPin size={24} />
                                                </div>
                                                <div className="card-content">
                                                    <h4>{wrapWithDefaultFont("ADDRESS")}</h4>
                                                    <p style={{ fontSize: '0.8em' }}>Kabiguru Road, Panchmatha More<br />
                                                    Saktinagar, Krishnanagar<br />
                                                    Nadia, West Bengal - 741102</p>
                                                </div>
                                            </div>
                                        </a>
                                    </div>
                                    
                                    {/* Right side - Google Maps */}
                                    <div className="contact-map">
                                        <iframe 
                                            src="https://maps.google.com/maps?q=23.386728599999998,88.5009809&z=18&t=k&output=embed" 
                                            width="100%" 
                                            height="100%" 
                                            style={{ border: 0 }} 
                                            allowFullScreen={true} 
                                            loading="lazy" 
                                            referrerPolicy="no-referrer-when-downgrade"
                                            className="map-iframe"
                                        ></iframe>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Content - Form */}
                    <motion.div
                        className="contact-right"
                        style={{ y: formY, opacity: formOpacity }}
                    >
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <input 
                                    type="text" 
                                    id="name" 
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required 
                                    placeholder=" " 
                                />
                                <label htmlFor="name">{wrapWithDefaultFont("YOUR NAME")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group">
                                <input 
                                    type="email" 
                                    id="email" 
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required 
                                    placeholder=" " 
                                />
                                <label htmlFor="email">{wrapWithDefaultFont("EMAIL ADDRESS")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group">
                                <input 
                                    type="text" 
                                    id="phone" 
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    placeholder=" " 
                                />
                                <label htmlFor="phone">{wrapWithDefaultFont("PHONE NUMBER")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group">
                                <select 
                                    id="service-type" 
                                    name="service"
                                    value={formData.service}
                                    onChange={handleInputChange}
                                    required
                                    className={formData.service ? "has-value" : ""}
                                >
                                    <option value="" disabled></option>
                                    <option value="wedding">Wedding</option>
                                    <option value="prewedding">Pre-Wedding</option>
                                    <option value="rice-ceremony">Rice Ceremony</option>
                                    <option value="baby-shower">Baby Shower</option>
                                    <option value="other">Other</option>
                                </select>
                                <label htmlFor="service-type">{wrapWithDefaultFont("SERVICE TYPE")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group">
                                <select 
                                    id="budget" 
                                    name="budget"
                                    value={formData.budget}
                                    onChange={handleInputChange}
                                    required
                                    className={formData.budget ? "has-value" : ""}
                                >
                                    <option value="" disabled></option>
                                    <option value="20000">₹20,000</option>
                                    <option value="40000">₹40,000</option>
                                    <option value="60000">₹60,000</option>
                                    <option value="80000">₹80,000</option>
                                    <option value="100000">₹1,00,000</option>
                                    <option value="120000">₹1,20,000</option>
                                    <option value="140000">₹1,40,000</option>
                                    <option value="160000">₹1,60,000</option>
                                    <option value="180000">₹1,80,000</option>
                                    <option value="180000+">₹1,80,000+</option>
                                </select>
                                <label htmlFor="budget">{wrapWithDefaultFont("BUDGET RANGE")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group">
                                <input 
                                    type="text" 
                                    id="date" 
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    required
                                    placeholder=" " 
                                    onFocus={(e) => e.target.type = 'date'} 
                                    onBlur={(e) => e.target.type = 'text'} 
                                />
                                <label htmlFor="date">{wrapWithDefaultFont("EVENT DATE")} *</label>
                                <div className="input-line"></div>
                            </div>

                            <div className="form-group full-width">
                                <textarea 
                                    id="message" 
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder=" " 
                                    rows={4}
                                ></textarea>
                                <label htmlFor="message">{wrapWithDefaultFont("TELL US YOUR STORY")}</label>
                                <div className="input-line"></div>
                            </div>

                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                                <span>{isSubmitting ? wrapWithDefaultFont("SENDING...") : wrapWithDefaultFont("SEND MESSAGE")}</span>
                                {isSubmitting ? (
                                    <div className="loading-spinner"></div>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>
                        </form>
                    </motion.div>

                    {/* Mobile Contact Info Container */}
                    <div className="contact-info-container-mobile">
                        <div className="contact-cards-mobile">
                            <a href="tel:+917908086574" className="contact-card-link">
                                <div className="contact-card">
                                    <div className="card-icon">
                                        <Phone size={24} />
                                    </div>
                                    <div className="card-content">
                                        <h4>{wrapWithDefaultFont("PHONE ")}</h4>
                                        <p>+91 79080 86574</p>
                                    </div>
                                </div>
                            </a>
                            
                            <a href="mailto:quick.studioz26@gmail.com" className="contact-card-link">
                                <div className="contact-card">
                                    <div className="card-icon">
                                        <Mail size={24} />
                                    </div>
                                    <div className="card-content">
                                        <h4>{wrapWithDefaultFont("EMAIL")}</h4>
                                        <p>quick.studioz26@gmail.com</p>
                                    </div>
                                </div>
                            </a>
                            
                            <a href="https://www.google.com/maps?q=23.386728599999998,88.5009809" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                                <div className="contact-card">
                                    <div className="card-icon">
                                        <MapPin size={24} />
                                    </div>
                                    <div className="card-content">
                                        <h4>{wrapWithDefaultFont("ADDRESS")}</h4>
                                        <p>Kabiguru Road, Panchmatha More<br />
                                        Saktinagar, Krishnanagar<br />
                                        Nadia, West Bengal - 741102</p>
                                    </div>
                                </div>
                            </a>
                        </div>
                        
                        <div className="contact-map-mobile">
                            <iframe 
                                src="https://maps.google.com/maps?q=23.386728599999998,88.5009809&z=18&t=k&output=embed" 
                                width="100%" 
                                height="100%" 
                                style={{ border: 0 }} 
                                allowFullScreen={true} 
                                loading="lazy" 
                                referrerPolicy="no-referrer-when-downgrade"
                                className="map-iframe"
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        className="success-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeSuccessModal}
                    >
                        <motion.div
                            className="success-modal"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="success-icon">
                                <Check size={48} />
                            </div>
                            <h3>{wrapWithDefaultFont("MESSAGE SENT!")}</h3>
                            <p>Thank you for reaching out to Studio Z26. We'll get back to you within 24-48 hours.</p>
                            <button className="modal-close-btn" onClick={closeSuccessModal}>
                                <span>{wrapWithDefaultFont("CLOSE")}</span>
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            {/* WhatsApp Floating Button */}
            <WhatsAppIcon />
        </div>
    );
};

export default ContactSection;
